import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, RouterLink } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';

import { ManageTabDropdownComponent } from './manage-tab-dropdown/manage-tab-dropdown.component';
import { ReportingTabDropdownComponent } from './reporting-tab-dropdown/reporting-tab-dropdown.component';
import { TaxCertificationTabDropdownComponent } from './tax-certification-tab-dropdown/tax-certification-tab-dropdown.component';
import {
  CheckPermissionsPipe,
  CheckSomePermissionsPipe,
} from '../../pipes/check-permissions/check-permisions.pipe';
import { TimeDiffPipe } from '../../pipes/time-diff/time-diff.pipe';
import { AuthApi } from '../../services/auth-api/auth-api.service';
import { IdleUserService } from '../../services/idle-user-service/idle-user-service.service';
import { NavigationHelperService } from '../../services/navigation-helper/navigation-helper.service';
import { ToastNotificationsManagerService } from '../../services/toast-notifications-manager/toast-notifications-manager.service';
import { TabColumnPermissions, TabPermissions } from '../../types/tab-permission.type';

@Component({
  selector: 'acp-navbar, [acp-navbar]',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  imports: [
    RouterLink,
    TaxCertificationTabDropdownComponent,
    ReportingTabDropdownComponent,
    ManageTabDropdownComponent,
    CheckPermissionsPipe,
    CheckSomePermissionsPipe,
    TimeDiffPipe
],
})
export class NavbarComponent implements OnInit, OnDestroy {
  sseObserver: Subscription;
  userInitials = 'JD';
  userFullName = 'John Doe';
  _onDestroy = new Subject<void>();

  isSidebarOpen = false;

  // Main navbar items permissions
  readonly viewBoProfilesPermission = 'bo-profiles:view';
  readonly viewBoFormsPermission = 'documents:view';
  readonly viewMarketInfoPermission = 'market-info:view';
  readonly viewTemplatesPermission = 'templates:view';
  readonly viewAuditsPermission = 'audits:view';
  readonly viewManageDropdownPermision = 'mdt:view-manage';
  //readonly viewNotificationsPermision = 'notifications:view';

  /**Updated, scalable way of dealing with multiple permissions
   * Using object helps with hiding the tab that need to be hidden if user has none of the required permissions
   * CheckPermissionPipe now accepts it and will allow the tab with dropdown to be rendered if user has at least
   * one of the permissions. If at least one tab is not protected though, the multiple permission check isn't needed.
   * TODO: This will be needed when moving all the permissionStrings into a service which feels like a good thing to do.
   */
  readonly reportingTabPermissions: TabPermissions = {
    viewPcpPosOvwPermission: 'pcp_act_ovw:view',
    viewPcpPosHistPermission: 'pcp_act_hist:view',
    boPositionsOverviewViewPermission: 'bo-positions:view',
    boPositionsHistoryViewPermission: 'bo-history:view',
    viewSPRPermission: 'spr:view',
    viewTaxLiabilitiesPermission: 'taxliability:view',
    viewSanctionsRawFilesPermission: 'sanction_screening_results:view',
    viewSanctionScreeningResultsPermission: 'sanction_screening_results:view',
    viewComplianceReportingSogeiPermission: 'sogei_compliance:view',
    viewPaymentMaturityReportsPermission: 'paymentmaturity:view',
    viewReportingPermission: 'reporting:view',
  };
  readonly taxCertTabPermissions: TabPermissions = {
    viewTaxCertificationsPermission: 'taxcert:view',
    viewDownstreamPositionsPermission: 'downstreampositions:view',
    viewQuickUpdates: 'taxcert:view',
  };
  readonly manageTabPermissions: TabPermissions = {
    /* Market Info Column 1 */
    viewDocumentTypesPermission: 'mdt:view-document-types',
    //viewExternalConfigPermission: 'mdt:view-external-config',
    viewReclaimStartDatesPermission: 'mdt:view-reclaim-start-dates',
    viewStatuteLimitPermission: 'mdt:view-statute-limit',
    viewMarketInfoPermission: 'market-info:view',
    //viewDocumentFieldsPermission: 'mdt:view-document-fields',
    /* Market Info Column 2 */
    viewDocumentValidityPermission: 'mdt:view-doc-validity',
    viewInternalConfigPermission: 'mdt:view-internal-config',
    viewRetroactiveTypesPermission: 'mdt:view-retroactive-types',
    viewStatutoryRatesPermission: 'mdt:view-statutory-rates',
    viewRequiredDOcuments: 'mdt:view-required-documents',
    /* Organization Components permissions */
    viewCompaniesPermission: 'mdt:view-companies',
    viewUsersPermission: 'users:view',
    viewRolesPermission: 'roles:view',
    /* Tax Certification Components */
    viewAuthSignPermission: 'signatory:view',
    viewAccountsPermission: 'accounts:view',
    viewCAEventDataPermission: 'caevent:view',
    viewProjectsPermission: 'projects:view',
    viewSecDataPermission: 'secdata:view',
    //viewGroupsPermission: 'groups:view',
    //viewActivityPermission: 'activity-permissions:view',
    //viewDatascopesPermission: 'datascopes:view',
    // emails
    viewEmailsPermission: 'email-types:view',
  };

  private readonly firstMarketColumn = [
    this.manageTabPermissions.viewDocumentTypesPermission,
    this.manageTabPermissions.viewExternalConfigPermission,
    this.manageTabPermissions.viewReclaimStartDatesPermission,
    this.manageTabPermissions.viewStatuteLimitPermission,
    this.manageTabPermissions.viewDocumentFieldsPermission,
  ];

  private readonly secondMarketColumn = [
    this.manageTabPermissions.viewDocumentValidityPermission,
    this.manageTabPermissions.viewInternalConfigPermission,
    this.manageTabPermissions.viewRetroactiveTypesPermission,
    this.manageTabPermissions.viewStatutoryRatesPermission,
    this.manageTabPermissions.viewRequiredDOcuments,
  ];

  readonly columnChecks: TabColumnPermissions = {
    firstMarketColumn: this.firstMarketColumn,
    secondMarketColumn: this.secondMarketColumn,
    marketInfoColumns: [...this.firstMarketColumn, ...this.secondMarketColumn],
  };

  constructor(
    readonly authApi: AuthApi,
    private navHelper: NavigationHelperService,
    private idleUserService: IdleUserService,
    private toastNotif: ToastNotificationsManagerService,
  ) {
    this.trackRouterChanges();
  }

  /* trackRouterChanges() will perform designated actions every time the route change */
  trackRouterChanges() {
    this.navHelper
      .getFilteredRouterEvents()
      .pipe(takeUntil(this._onDestroy))
      .subscribe(event => {
        if (event instanceof NavigationEnd && this.isSidebarOpen) this.isSidebarOpen = false;
      });
  }

  /**
   * setUserInfo() will retrieve information about current user and update navbar
   */
  setUserInfo() {
    const userInfo = this.authApi.getUserInfo();
    if (typeof userInfo?.UserFirstName === 'string' && typeof userInfo?.UserLastName === 'string') {
      this.userInitials =
        userInfo.UserFirstName.substring(0, 1) + userInfo.UserLastName.substring(0, 1);
      this.userFullName = userInfo.UserFirstName + ' ' + userInfo.UserLastName;
    }
  }

  /**
   * isInt returns true if n is an integer and false otherwise
   * @param n
   * @returns boolean
   */
  isInt(n: any): boolean {
    return Number(n) === n && n % 1 === 0;
  }

  logout(): void {
    this.authApi.logout();
  }

  get sessionExpiresDiff() {
    return this.idleUserService.sessionExpiresDiff;
  }
  get idlePercentage() {
    return this.idleUserService.idlePercentage;
  }

  ngOnInit(): void {
    this.authApi.setPermissions();
    this.setUserInfo();

    this.idleUserService.userInactive.subscribe(isIdle => {
      if (isIdle) {
        this.logout();
        this.toastNotif.addNew({
          type: 'warning',
          content: 'You were logged out due to inactivity',
          hideAfterMs: 0,
        });
      }
    });
  }

  ngOnDestroy() {
    if (this.sseObserver) this.sseObserver.unsubscribe();
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  toggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
  }
}
